import { DocumentTypes } from "@/prisma-package/src";
import {
  SerializedClassifier,
  SerializedRegex,
} from "@/prisma-package/src/utils/document-type-builtins";

/**
 * Data-driven document type detection. Port of the old hardcoded
 * DOCUMENT_PATTERNS scorer in detect-document-type.ts with identical
 * semantics, generalized to score against classifier definitions from the
 * DocumentTypeDefinition registry (built-ins + admin-created custom types).
 */

// Minimal definition shape the scorer needs. claimThreshold is the type's
// calibrated minimum claim score (DocumentTypeCalibration); absent = the
// type claims at any score > 0.
export type ClassifiableDefinition = {
  key: string;
  classifier: SerializedClassifier | null;
  claimThreshold?: number | null;
};

// One matched pattern with the context needed to explain a score.
export type PatternMatch = {
  source: string;
  weight: number;
  kind: "text" | "filename";
};

export interface DocumentTypeScore {
  type: string;
  score: number;
  matches: PatternMatch[];
  excluded: boolean;
  exclusionReason?: string;
}

export interface DocumentTypeDetection {
  type: string;
  confidence: "high" | "medium" | "low";
  reason: string;
  results: DocumentTypeScore[];
}

const FILENAME_BONUS = 3;

// Confidence bands, shared with corpus-validation's QUALIFY_SCORE /
// POSSIBLE_SCORE. Deliberately NOT a claim floor: on real labeled corpora
// (see docs/doc-type-pattern-analysis-the-last-time.md) weak best-matches
// were right far more often than wrong, so detection claims the best type
// at any score > 0 and reports low confidence instead of falling to OTHER.
// Per-type calibrated thresholds are the planned replacement for a floor.
export const HIGH_CONFIDENCE_SCORE = 15;
export const MEDIUM_CONFIDENCE_SCORE = 8;

const compileRegex = (serialized: SerializedRegex): RegExp | null => {
  try {
    return new RegExp(serialized.source, serialized.flags || "i");
  } catch {
    // Never let a bad admin-saved pattern break detection.
    return null;
  }
};

/**
 * Pure, console-silent scorer. Returns the full per-type breakdown plus the
 * chosen type so callers (and the admin pattern preview) can inspect exactly
 * why a document was classified the way it was. `detectDocumentType` is a
 * thin wrapper around this.
 */
export function scoreDocumentType(
  documentText: string,
  definitions: ClassifiableDefinition[],
  fileName?: string
): DocumentTypeDetection {
  const text = documentText.toLowerCase().trim();
  const results: DocumentTypeScore[] = [];

  for (const definition of definitions) {
    const classifier = definition.classifier;
    if (!classifier || classifier.patterns.length === 0) continue;

    let score = 0;
    const matchedPatterns: PatternMatch[] = [];
    let excluded = false;
    let exclusionReason: string | undefined;

    // Check exclusion patterns first
    for (const exclusion of classifier.exclusionPatterns ?? []) {
      const regex = compileRegex(exclusion);
      if (regex?.test(text)) {
        excluded = true;
        exclusionReason = exclusion.source;
        break;
      }
    }

    if (excluded) {
      results.push({
        type: definition.key,
        score: 0,
        matches: [],
        excluded: true,
        exclusionReason,
      });
      continue;
    }

    // Check weighted patterns
    for (const weightedPattern of classifier.patterns) {
      const regex = compileRegex(weightedPattern);
      if (regex?.test(text)) {
        score += weightedPattern.weight;
        matchedPatterns.push({
          source: weightedPattern.source,
          weight: weightedPattern.weight,
          kind: "text",
        });
      }
    }

    // Bonus points for filename match
    if (fileName) {
      for (const filenamePattern of classifier.filenamePatterns ?? []) {
        const regex = compileRegex(filenamePattern);
        if (regex?.test(fileName.toLowerCase())) {
          score += FILENAME_BONUS;
          matchedPatterns.push({
            source: filenamePattern.source,
            weight: FILENAME_BONUS,
            kind: "filename",
          });
          break;
        }
      }
    }

    if (score > 0) {
      results.push({
        type: definition.key,
        score,
        matches: matchedPatterns,
        excluded: false,
      });
    }
  }

  // Sort scored (non-excluded) results by score (highest first)
  const scored = results
    .filter((result) => !result.excluded && result.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length === 0) {
    return {
      type: DocumentTypes.OTHER,
      confidence: "low",
      reason: "no patterns matched",
      results,
    };
  }

  // Calibrated claim thresholds: a type only competes for the claim when its
  // score meets its own threshold. Uncalibrated types claim at any score.
  const thresholdByKey = new Map(
    definitions
      .filter((definition) => typeof definition.claimThreshold === "number")
      .map((definition) => [definition.key, definition.claimThreshold as number])
  );
  const eligible = scored.filter(
    (result) => result.score >= (thresholdByKey.get(result.type) ?? 1)
  );

  if (eligible.length === 0) {
    return {
      type: DocumentTypes.OTHER,
      confidence: "low",
      reason: `no type met its claim threshold (best ${scored[0].type} scored ${scored[0].score} < ${thresholdByKey.get(scored[0].type)})`,
      results,
    };
  }

  const bestMatch = eligible[0];

  const confidence: "high" | "medium" | "low" =
    bestMatch.score >= HIGH_CONFIDENCE_SCORE
      ? "high"
      : bestMatch.score >= MEDIUM_CONFIDENCE_SCORE
        ? "medium"
        : "low";

  return {
    type: bestMatch.type,
    confidence,
    reason: `best match ${bestMatch.type} (score ${bestMatch.score})`,
    results,
  };
}

export function detectDocumentType(
  documentText: string,
  definitions: ClassifiableDefinition[],
  fileName?: string
): string {
  return scoreDocumentType(documentText, definitions, fileName).type;
}
